import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { authApi } from 'auth/auth.api';
import { useAuth } from 'auth/AuthProvider';
import {
  buildProfilePatch,
  firstInvalidField,
  focusField,
  validateProfileValues,
  valuesFromUser,
} from 'auth/profileForm';
import { invalidateAfterProfileOrLocale } from 'core/api/invalidation';
import { collectFieldErrors, firstFieldError, humanizeApiError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { ProfileFormFields } from 'features/profile/ProfileFormFields';
import { useProfileOptions } from 'features/profile/useProfileOptions';
import { Button } from 'shared/components/Button';
import { Card, ErrorState, Skeleton } from 'shared/components/Feedback';
import { LanguageSwitcher } from 'shared/components/LanguageSwitcher';
import { PageMeta } from 'shared/components/PageMeta';
import { useToast } from 'shared/components/Toast';
import { useSubmitLock } from 'shared/hooks/useSubmitLock';
import { maskPhone } from 'shared/utilities/format';

function mapProfileFieldErrors(errors) {
  const keys = [
    'full_name',
    'age_range',
    'education_level',
    'is_employed',
    'work_experience',
    'experience_field',
    'skills',
    'country',
  ];
  const next = {};
  keys.forEach((key) => {
    const message = collectFieldErrors(errors, key)[0] || firstFieldError(errors, key);
    if (message) {
      next[key] = message;
    }
  });
  return next;
}

export function ProfilePage() {
  const { t, locale, setLocale } = useI18n();
  const { user, logout, applyUser, refreshUser } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { pending, tryAcquire, release } = useSubmitLock();
  const optionsQuery = useProfileOptions();
  const options = optionsQuery.data || null;
  const [values, setValues] = useState(() => valuesFromUser(user, options));
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (!options) {
      return;
    }
    setValues(valuesFromUser(user, options));
  }, [options, user]);

  function updateField(name, value) {
    setValues((current) => ({ ...current, [name]: value }));
    setFieldErrors((current) => {
      if (!current[name]) {
        return current;
      }
      const next = { ...current };
      delete next[name];
      return next;
    });
  }

  async function handleLocale(nextLocale) {
    const previous = locale;
    try {
      const updated = await authApi.updateProfile({ preferred_locale: nextLocale });
      applyUser(updated);
      await invalidateAfterProfileOrLocale(queryClient);
    } catch (err) {
      setLocale(previous);
      toast.error(humanizeApiError(err, t));
    }
  }

  async function handleSave(event) {
    event.preventDefault();
    if (!options || !tryAcquire()) {
      return;
    }
    const localErrors = validateProfileValues(values, options, t, { mode: 'edit' });
    if (Object.keys(localErrors).length > 0) {
      setFieldErrors(localErrors);
      setFormError(t('errors.validation'));
      focusField(firstInvalidField(localErrors));
      release();
      return;
    }
    const patch = buildProfilePatch(values, user, options);
    setFormError('');
    setFieldErrors({});
    try {
      if (Object.keys(patch).length > 0) {
        const updated = await authApi.updateProfile(patch);
        applyUser(updated);
        try {
          await refreshUser();
        } catch {
          applyUser(updated);
        }
        await invalidateAfterProfileOrLocale(queryClient);
      }
      toast.success(t('profile.saved'));
      release();
    } catch (err) {
      const mapped = mapProfileFieldErrors(err.errors || {});
      setFieldErrors(mapped);
      setFormError(humanizeApiError(err, t));
      focusField(firstInvalidField(mapped));
      toast.error(humanizeApiError(err, t));
      release();
    }
  }

  async function handleLogout() {
    await logout();
    navigate(paths.welcome, { replace: true });
  }

  return (
    <div className="profile-page">
      <PageMeta titleKey="meta.profile" />
      <h1>{t('profile.title')}</h1>
      <div className="profile-grid">
        <Card>
          <form className="profile-edit-form" onSubmit={handleSave} aria-busy={optionsQuery.isLoading || undefined}>
            <h2>{t('profile.details')}</h2>
            <dl className="detail-list">
              <div>
                <dt>{t('profile.phone')}</dt>
                <dd>{maskPhone(user?.phone)}</dd>
              </div>
              <div>
                <dt>{t('profile.locale')}</dt>
                <dd><LanguageSwitcher onChange={handleLocale} /></dd>
              </div>
            </dl>
            {optionsQuery.isLoading ? <Skeleton rows={6} /> : null}
            {optionsQuery.isError ? (
              <ErrorState
                title={t('profile.optionsFailed')}
                onRetry={() => optionsQuery.refetch()}
                retryLabel={t('actions.retry')}
                homeAction={false}
              />
            ) : null}
            {options ? (
              <ProfileFormFields
                t={t}
                values={values}
                options={options}
                errors={fieldErrors}
                disabled={pending}
                onChange={updateField}
              />
            ) : null}
            {formError ? <p className="field-error" role="alert">{formError}</p> : null}
            <Button type="submit" loading={pending} disabled={!options || optionsQuery.isError} data-testid="profile-save">
              {t('actions.save')}
            </Button>
          </form>
        </Card>
        <Card>
          <h2>{t('profile.links')}</h2>
          <div className="link-stack">
            <Link to={paths.history}>{t('actions.viewHistory')}</Link>
            <Link to={paths.consultations}>{t('nav.consultations')}</Link>
            <Link to={paths.notifications}>{t('nav.notifications')}</Link>
            <Link to={paths.support}>{t('nav.support')}</Link>
            <Link to={paths.terms}>{t('nav.terms')}</Link>
            <Link to={paths.privacy}>{t('nav.privacy')}</Link>
            <Button tone="ghost" onClick={handleLogout}>{t('actions.logout')}</Button>
          </div>
        </Card>
      </div>
      <Card className="danger-zone">
        <h2>{t('profile.danger')}</h2>
        <div className="action-row">
          <Link className="btn btn-danger" to={paths.accountDelete}>{t('actions.delete')}</Link>
        </div>
      </Card>
    </div>
  );
}
